// src/store/slices/departmentSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import departmentApi from 'api/departmentApi';

/* ------------------------------------------------------------------ */
/* Async Thunks */
/* ------------------------------------------------------------------ */

export const fetchDepartments = createAsyncThunk(
  'department/fetchDepartments',
  async ({ page = 1, pageSize = 10, search = '', is_active }, { rejectWithValue }) => {
    try {
      const response = await departmentApi.getAll(page, pageSize, search, is_active);
      return response; // { data: [...], pagination: { ... } }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load departments';
      return rejectWithValue(msg);
    }
  }
);

export const createDepartment = createAsyncThunk(
  'department/createDepartment',
  async (data, { rejectWithValue }) => {
    try {
      const response = await departmentApi.create(data);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create department';
      return rejectWithValue(msg);
    }
  }
);

export const updateDepartment = createAsyncThunk(
  'department/updateDepartment',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await departmentApi.update(id, updates);
      return response;
    } catch (err) {
      const msg = err.response?.data?.name.error || 'Failed to update department';
      return rejectWithValue(msg);
    }
  }
);

export const deleteDepartment = createAsyncThunk(
  'department/deleteDepartment',
  async (id, { rejectWithValue }) => {
    try {
      await departmentApi.delete(id);
      return id;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete department';
      return rejectWithValue(msg);
    }
  }
);

/* -------------------------- EXPORT -------------------------- */
export const exportDepartments = createAsyncThunk(
  'department/exportDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await departmentApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `departments_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

/* -------------------------- IMPORT PREVIEW -------------------------- */
export const importPreview = createAsyncThunk(
  'department/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await departmentApi.importPreview(file);
      return data; // { preview: [...], total: number }
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Preview failed');
    }
  }
);

/* -------------------------- IMPORT CONFIRM -------------------------- */
export const importConfirm = createAsyncThunk(
  'department/importConfirm',
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await departmentApi.importConfirm(preview);
      // after success, refetch the list
      dispatch(fetchDepartments({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Import failed');
    }
  }
);

/* ------------------------------------------------------------------ */
/* Slice */
/* ------------------------------------------------------------------ */

const departmentSlice = createSlice({
  name: 'department',
  initialState: {
    departments: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* FETCH */
      .addCase(fetchDepartments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* CREATE – optimistic push */
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.departments.unshift(action.payload);
        state.pagination.count += 1;
      })

      /* UPDATE – replace in-place */
      .addCase(updateDepartment.fulfilled, (state, action) => {
        const idx = state.departments.findIndex((d) => d.id === action.payload.id);
        if (idx !== -1) state.departments[idx] = action.payload;
      })

      /* DELETE – filter out */
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.departments = state.departments.filter((d) => d.id !== action.payload);
        state.pagination.count -= 1;
      });

      /* EXPORT – nothing to store */
    builder.addCase(exportDepartments.rejected, (state, action) => {
      state.error = action.payload;
    });

    /* IMPORT PREVIEW */
    builder
      .addCase(importPreview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importPreview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.importPreview = action.payload.preview;
        state.importTotal = action.payload.total;
      })
      .addCase(importPreview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    /* IMPORT CONFIRM */
    builder
      .addCase(importConfirm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importConfirm.fulfilled, (state) => {
        state.isLoading = false;
        state.importPreview = null;
        state.importTotal = null;
      })
      .addCase(importConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = departmentSlice.actions;
export default departmentSlice.reducer;